import { useMemo } from 'react';
import { ReactComponent as CriticalAssetIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import {
  AvatarGroup,
  ChipCounterList,
  FlexCenter,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { Column } from '@elementary/common-ui/components/Table';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import {
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
} from '@elementary/common-ui/constants/urlParams';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import { Box, Link, Tooltip } from '@mui/material';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import styled from 'styled-components';
import StatusChipsRenderer from '../renderers/StatusChipsRenderer';
import coverageRenderer from '../renderers/coverageRenderer';
import testNamesRenderer from '../renderers/testNamesRenderer';
import { CoverageTableRow } from '../types';
import { useCoverageFilterOptions } from './useCoverageFilterOptions';

const StyledFlexCenter = styled(FlexCenter)`
  gap: 8px;
  justify-content: flex-start;
`;

const StyledOverflowTooltip = styled(OverflowTooltip)`
  width: fit-content;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

// todo: move this into ChipCounterList and this wrappers from all pages
const ChipCounterListWrapper = styled(Box)<{ $moreThanOne?: boolean }>`
  .MuiChip-root {
    max-width: ${({ $moreThanOne }) => ($moreThanOne ? '70%' : '100%')};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const useCoverageTableColumns = (daysBack: number) => {
  const { formatUrlParam } = useSelectedTreeItem();
  const navigate = useNavigateWithEnv();
  const { treeType, treeViewBy } = useTreeViewParams();
  const { filterOptions } = useCoverageFilterOptions(daysBack);

  return useMemo<Array<Column<CoverageTableRow>>>(
    () => [
      {
        id: 'name',
        header: 'Asset name',
        filterOperators: ['equals'],
        filterDropdownWidth: 350,
        filterOptionsOverride: filterOptions.names,

        renderer: ({ name, isCritical, id }) => (
          <StyledFlexCenter>
            <StyledOverflowTooltip tooltipContent={id ?? name}>
              <StyledLink
                onClick={(e) => {
                  const newParams = new URLSearchParams();
                  const urlParam = formatUrlParam(id);

                  if (urlParam) {
                    if (treeType) {
                      newParams.set(TREE_TYPE, treeType);
                    }
                    if (treeViewBy) {
                      newParams.set(TREE_VIEW_BY, treeViewBy);
                    }
                    newParams.set(TREE_NODE, urlParam);
                    navigate(`/catalog?${newParams?.toString()}`);
                    e.stopPropagation();
                  }
                }}
              >
                {name}
              </StyledLink>
            </StyledOverflowTooltip>

            {isCritical && (
              <Tooltip title="Critical Asset" placement="top">
                <FlexCenter>
                  <CriticalAssetIcon width={20} />
                </FlexCenter>
              </Tooltip>
            )}
          </StyledFlexCenter>
        ),
      },
      {
        id: 'dbName',
        header: 'Database',
        filterOperators: ['equals'],
        filterOptionsOverride: filterOptions.dbNames,
        defaultUntoggled: true,
      },
      {
        id: 'schemaName',
        header: 'Schema',
        filterOperators: ['equals'],
        filterOptionsOverride: filterOptions.schemaNames,
        defaultUntoggled: true,
      },
      {
        id: 'coverage.percentageAmount',
        header: 'Coverage',
        isFilterCounterHidden: true,
        filterOperators: ['equals'],
        sortFilterOptions: false,
        renderer: (rowData) =>
          coverageRenderer(rowData.coverage.percentageAmount),
        filterOptionsOverride: filterOptions.percentageAmount,
      },
      {
        id: 'coverage.freshness',
        header: 'Freshness',
        filterOperators: ['equals'],
        hideFromTableFilters: true,
        filterOptionsOverride: filterOptions.freshness,
        renderer: (rowData) => (
          <StatusChipsRenderer
            assetId={rowData.id}
            statuses={rowData.coverage.freshness}
            qualityDimension="freshness"
          />
        ),
      },
      {
        id: 'coverage.completeness',
        header: 'Completeness',
        filterOperators: ['equals'],
        hideFromTableFilters: true,
        filterOptionsOverride: filterOptions.completeness,
        renderer: (rowData) => (
          <StatusChipsRenderer
            assetId={rowData.id}
            statuses={rowData.coverage.completeness}
            qualityDimension="completeness"
          />
        ),
      },
      {
        id: 'coverage.uniqueness',
        header: 'Uniqueness',
        filterOperators: ['equals'],
        hideFromTableFilters: true,
        filterOptionsOverride: filterOptions.uniqueness,
        renderer: (rowData) => (
          <StatusChipsRenderer
            assetId={rowData.id}
            statuses={rowData.coverage.uniqueness}
            qualityDimension="uniqueness"
          />
        ),
      },
      {
        id: 'coverage.validity',
        header: 'Validity',
        filterOperators: ['equals'],
        hideFromTableFilters: true,
        filterOptionsOverride: filterOptions.validity,
        renderer: (rowData) => (
          <StatusChipsRenderer
            assetId={rowData.id}
            statuses={rowData.coverage.validity}
            qualityDimension="validity"
          />
        ),
      },
      {
        id: 'coverage.accuracy',
        header: 'Accuracy',
        filterOperators: ['equals'],
        hideFromTableFilters: true,
        filterOptionsOverride: filterOptions.accuracy,
        renderer: (rowData) => (
          <StatusChipsRenderer
            assetId={rowData.id}
            statuses={rowData.coverage.accuracy}
            qualityDimension="accuracy"
          />
        ),
      },
      {
        id: 'coverage.consistency',
        header: 'Consistency',
        filterOperators: ['equals'],
        hideFromTableFilters: true,
        filterOptionsOverride: filterOptions.consistency,
        renderer: (rowData) => (
          <StatusChipsRenderer
            assetId={rowData.id}
            statuses={rowData.coverage.consistency}
            qualityDimension="consistency"
          />
        ),
      },
      {
        id: 'coverage.other',
        header: 'Other',
        hideFromTableFilters: true,
        filterOperators: ['equals'],
        renderer: (rowData) => (
          <StatusChipsRenderer
            assetId={rowData.id}
            statuses={rowData.coverage.other}
            qualityDimension="other"
          />
        ),
        filterOptionsOverride: filterOptions.other,
      },
      {
        id: 'tags',
        header: 'Tags',
        filterOperators: ['equals'],
        defaultUntoggled: true,
        sortFilterOptions: false,
        filterOptionsOverride: filterOptions.tags,
        renderer: ({ tags }) => (
          <ChipCounterListWrapper $moreThanOne={tags.length > 1}>
            <ChipCounterList items={tags} />
          </ChipCounterListWrapper>
        ),
      },
      {
        id: 'owners',
        header: 'Owners',
        filterOperators: ['equals'],
        defaultUntoggled: true,
        sortFilterOptions: false,
        filterOptionsOverride: filterOptions.owners,
        renderer: ({ owners }) => <AvatarGroup items={owners} />,
      },
      {
        id: 'testNames',
        header: 'Test names',
        showValueWhenEmpty: true,
        defaultUntoggled: true,
        filterOptionsOverride: filterOptions.testNames,
        renderer: testNamesRenderer,
      },
    ],
    [formatUrlParam, navigate, treeType, treeViewBy, filterOptions],
  );
};
